import { create } from 'zustand'

import { friendlyMessage } from '../lib/friendly-error'

export type Severity = 'success' | 'info' | 'warning' | 'error'

interface Snack {
  id: number
  severity: Severity
  message: string
}

interface SnackbarState {
  items: Snack[]
  show: (severity: Severity, message: string) => void
  dismiss: (id: number) => void
}

let nextId = 1

export const useSnackbarStore = create<SnackbarState>()((set) => ({
  items: [],
  show: (severity, message) =>
    set((s) => ({ items: [...s.items, { id: nextId++, severity, message }] })),
  dismiss: (id) => set((s) => ({ items: s.items.filter((i) => i.id !== id) })),
}))

// Convenience helpers usable outside React (e.g. from mutation onError).
// `error` filters its input through `friendlyMessage` so raw stack traces,
// SQL errors etc. from the server never reach the user — see
// `lib/friendly-error.ts` for the heuristic.
//
// Three accepted call shapes (all backward-compatible with the old
// `toast.error('string')` form):
//   toast.error('User-readable string')      // string is filtered too
//   toast.error(e)                            // Error/ApiError, falls back to generic
//   toast.error(e, 'Failed to save')          // explicit fallback when e is technical
export const toast = {
  success: (m: string) => useSnackbarStore.getState().show('success', m),
  error: (input: unknown, fallback?: string) => {
    const msg = friendlyMessage(input, fallback)
    useSnackbarStore.getState().show('error', msg)
  },
  info: (m: string) => useSnackbarStore.getState().show('info', m),
  warn: (m: string) => useSnackbarStore.getState().show('warning', m),
}
